import * as React from 'react';
import { connectSsr } from 'ssr-service';
import NotFound from '../Errors/NotFound';
import API from '../../services/API';
import Product, { ProductProps } from '../Product/Product';
import styled from 'styled-components';
import Category, { CategoryProps } from '../Category/Category';
import CMS, { CmsProps } from '../CMS/CMS';
import { connect } from 'react-redux';
import {
  FURNITURE_CATEGORY_GROUP_UNIQID,
  SCHOOL_ARTICLES_UNIQID,
  UrlMapTypes,
} from '../../constants';
import { rem } from 'polished';
import { __ } from 'react-i18n/lib';
import ShopDetail, { ShopDetailProps } from '../ShopDetail/ShopDetail';
import Redirect from '../Errors/Redirect';
import Sets, { SetsProps } from '../Sets/Sets';
import {
  setCurrentCategory,
  setCurrentCategoryGroupId,
  setCurrentCategoryId,
  setUrlMapData,
} from '../App/actions';
import { Loader } from '../../components/_helpers/Loader/Loader';
import { WithRouterProps } from 'react-router';
import SchoolArticle, {
  SchoolArticleProps,
} from '../SchoolArticle/SchoolArticle';
import FurnitureCategoryGroup from '../Category/FurnitureCategoryGroup';

enum UrlType {
  Product,
  NotFound,
  Category,
  CategoryWithSubcategories,
  Sitemap,
  SitemapWithContent,
  Shop,
  Redirect,
  Brand,
  School,
  CategoryGroup,
  ProductSale,
  SchoolArticle,
}

interface Props {
  dispatch: any;
  urlType: UrlType;
  isPreview: boolean;
  childProps: object;
  loading: boolean;
  isError: boolean;
  token: any;
  urlMapData: any;
}

interface ChildProps {
  urlType: UrlType;
  childProps: ProductProps | CategoryProps | null;
  urlMapData?: any;
}

class UrlMapper extends React.Component<Props & WithRouterProps, {}> {
  public static defaultProps = {
    loading: true,
    isError: false,
  };

  public static async getInitialProps(props) {
    let newProps: ChildProps;
    let isPreview: boolean;
    let urlMapData: any;

    try {
      isPreview = UrlMapper.isPreview(props.location.pathname);

      urlMapData = await API.getUrlMap(
        {
          url: props.location.pathname.replace('/preview/', ''),
        },
        {
          xAcceptLanguage: props.lang,
        },
      );

      props.dispatch(setUrlMapData(urlMapData));

      const { data, type } = urlMapData;

      switch (type) {
        case UrlMapTypes.product: {
          newProps = await UrlMapper.getProductChildProps(
            data.product_id,
            props,
          );
          break;
        }
        case UrlMapTypes.brand: {
          newProps = await UrlMapper.getBrandChildProps(data.brand_id, props);
          break;
        }
        case UrlMapTypes.category: {
          newProps = await UrlMapper.getCategoryChildProps(
            data.category_id,
            props,
          );
          props.dispatch(setCurrentCategory(data.category_id));
          break;
        }
        case UrlMapTypes.CategoryGroup: {
          newProps = await UrlMapper.getCategoryGroupChildProps(
            data.category_group_id,
            props,
          );
          props.dispatch(setCurrentCategoryGroupId(data.category_group_id));
          break;
        }
        case UrlMapTypes.Sitemap: {
          newProps = await UrlMapper.getSitemapChildProps(
            data.sitemap_id,
            props,
          );
          break;
        }
        case UrlMapTypes.SitemapWithContent: {
          // @ts-ignore
          if (data?.uniqid === SCHOOL_ARTICLES_UNIQID) {
            newProps = await UrlMapper.getSchoolArticleChildProps(
              data.sitemap_id,
              data.content_id,
              props,
            );
          } else {
            newProps = await UrlMapper.getSitemapWithContentChildProps(
              data.sitemap_id,
              data.content_id,
              props,
            );
          }
          break;
        }
        case UrlMapTypes.Shop: {
          newProps = await UrlMapper.getShopChildProps(data.web_shop_id, props);
          break;
        }
        case UrlMapTypes.school: {
          newProps = await UrlMapper.getSetChildProps(data.club_user_id, props);
          break;
        }

        case UrlMapTypes.productSale: {
          newProps = await UrlMapper.getProductSaleChildProps(
            data.category_group_id,
            props,
          );
          props.dispatch(setCurrentCategoryGroupId(data.category_group_id));
          break;
        }

        case UrlMapTypes.redirect: {
          newProps = await UrlMapper.getRedirectChildProps(urlMapData);
          break;
        }

        default: {
          newProps = await UrlMapper.getNotFoundChildProps(urlMapData);
          break;
        }
      }
    } catch (exp) {
      return {
        isError: true,
      };
    }

    return { ...newProps, isPreview, urlMapData };
  }

  private static getProductChildProps = async (productId, props) => {
    const productProps = {
      product_id: productId,
      ...props,
    };
    return {
      urlType: UrlType.Product,
      childProps: await Product.getInitialProps(productProps),
    };
  };

  private static getSitemapWithContentChildProps = async (
    sitemapId,
    contentId,
    props,
  ) => {
    return {
      urlType: UrlType.SitemapWithContent,
      childProps: await CMS.getInitialProps({
        sitemapId,
        contentId,
        parentProps: props,
      }),
    };
  };

  private static getSchoolArticleChildProps = async (
    sitemapId,
    contentId,
    props,
  ) => {
    return {
      urlType: UrlType.SchoolArticle,
      childProps: await SchoolArticle.getInitialProps({
        sitemapId,
        contentId,
        dispatch: props.dispatch,
        parentProps: props,
      }),
    };
  };

  private static getShopChildProps = async (webShopId, props) => {
    return {
      urlType: UrlType.Shop,
      childProps: await ShopDetail.getInitialProps({
        webShopId,
        ...props,
      }),
    };
  };

  private static getSetChildProps = async (clubUserId, props) => {
    return {
      urlType: UrlType.School,
      childProps: await Sets.getInitialProps({
        clubUserId,
        ...props,
      }),
    };
  };

  private static getSitemapChildProps = async (sitemapId, props) => {
    return {
      urlType: UrlType.Sitemap,
      childProps: await CMS.getInitialProps({
        sitemapId,
        parentProps: props,
      }),
    };
  };

  private static getCategoryChildProps = async (categoryId, props) => {
    const categoryProps = {
      category_id: categoryId,
    };

    const categoryData = await API.loadCategory(
      categoryProps.category_id || 0,
      undefined,
      { xAcceptLanguage: props.lang },
    );
    props.dispatch(setCurrentCategoryId(categoryId));
    return {
      urlType: UrlType.Category,
      childProps: {
        category: categoryData,
        location: props.location,
        dispatch: props.dispatch,
      },
    };
  };

  private static getProductSaleChildProps = async (categoryGroupId, props) => {
    const categoryGroupProps = {
      categoryGroupId,
    };

    const categoryData = await API.loadCategoryGroup(
      categoryGroupProps.categoryGroupId || 0,
      props,
    );

    return {
      urlType: UrlType.CategoryGroup,
      childProps: {
        category: categoryData,
        location: props.location,
        dispatch: props.dispatch,
        oldPrice: 1,
      },
    };
  };

  private static getCategoryGroupChildProps = async (
    categoryGroupId,
    props,
  ) => {
    const categoryGroupProps = {
      categoryGroupId,
    };

    const categoryData = await API.loadCategoryGroup(
      categoryGroupProps.categoryGroupId || 0,
      props,
    );

    return {
      urlType: UrlType.CategoryGroup,
      childProps: {
        category: categoryData,
        location: props.location,
        dispatch: props.dispatch,
      },
    };
  };

  private static getBrandChildProps = async (brandId, props) => {
    const brandProps = {
      brand_id: brandId,
    };

    const brandData = await API.loadBrandDetail(
      brandProps.brand_id || 0,
      undefined,
      { xAcceptLanguage: props.lang || 'sk' },
    );

    return {
      urlType: UrlType.Brand,
      childProps: {
        brand: brandData,
        location: props.location,
        dispatch: props.dispatch,
      },
    };
  };

  private static getNotFoundChildProps = async (urlMapData) => {
    return {
      urlType: UrlType.NotFound,
      childProps: await NotFound.getInitialProps({
        urlMapData,
      }),
      urlMapData,
    };
  };

  private static getRedirectChildProps = async (urlMapData) => {
    return {
      urlType: UrlType.Redirect,
      childProps: await NotFound.getInitialProps({
        urlMapData,
      }),
      urlMapData,
    };
  };

  private static getLoaderView = () => {
    return (
      <ContentStyle>
        <Loader loading={true} />
      </ContentStyle>
    );
  };

  private static isPreview = (url: string): boolean => {
    return !!url.match(/\/preview\/.+/);
  };

  public render() {
    const { isError, urlType, loading, urlMapData } = this.props;

    if (isError) {
      return this.getNotFoundView();
    }

    if (loading) {
      return UrlMapper.getLoaderView();
    }

    if (
      urlType === UrlType.CategoryGroup &&
      urlMapData?.data?.uniqid === FURNITURE_CATEGORY_GROUP_UNIQID
    ) {
      return this.getFurnitureCategoryGroupView();
    } else if (
      urlType === UrlType.Category ||
      urlType === UrlType.CategoryGroup ||
      urlType === UrlType.ProductSale
    ) {
      return this.getCategoryView();
    } else if (
      urlType === UrlType.Sitemap ||
      urlType === UrlType.SitemapWithContent
    ) {
      return this.getCMSView();
    } else if (urlType === UrlType.CategoryWithSubcategories) {
      return this.getCategoryView();
    } else if (urlType === UrlType.Product) {
      return this.getProductView();
    } else if (urlType === UrlType.Brand) {
      return this.getCategoryView();
    } else if (urlType === UrlType.Shop) {
      return this.getShopView();
    } else if (urlType === UrlType.School) {
      return this.getSetsView();
    } else if (urlType === UrlType.SchoolArticle) {
      return this.getSchoolArticleView();
    } else if (urlType === UrlType.Redirect) {
      return this.getRedirectView();
    }

    return this.getNotFoundView();
  }

  private getNotFoundView = () => {
    return <NotFound {...this.props.childProps} />;
  };

  private getRedirectView = () => {
    return <Redirect {...this.props.childProps} />;
  };

  private getCategoryView = () => {
    const childProps: CategoryProps = this.props.childProps as CategoryProps;
    return <Category {...childProps} loading={this.props.loading} />;
  };

  private getFurnitureCategoryGroupView = () => {
    const childProps: CategoryProps = this.props.childProps as CategoryProps;
    return <FurnitureCategoryGroup {...childProps} />;
  };

  private getCMSView = () => {
    const childProps: CmsProps = this.props.childProps as CmsProps;

    return (
      <>
        {this.props.isPreview && (
          <UpperBar>
            {__(
              'Toto je náhľad stránky. Ak je stránka v administrácii označená ako “Zobrazená” je tento náhľad verejne dostupný.',
            )}
          </UpperBar>
        )}
        <CMS {...childProps} location={this.props.location} />
      </>
    );
  };

  private getProductView = () => {
    const childProps: ProductProps = this.props.childProps as ProductProps;
    return <Product {...childProps} />;
  };

  private getShopView = () => {
    const childProps: ShopDetailProps = this.props
      .childProps as ShopDetailProps;
    return <ShopDetail {...childProps} />;
  };

  private getSetsView = () => {
    const childProps: SetsProps = this.props.childProps as SetsProps;
    return <Sets {...childProps} />;
  };

  private getSchoolArticleView = () => {
    const childProps: SchoolArticleProps = this.props
      .childProps as SchoolArticleProps;
    return <SchoolArticle {...childProps} />;
  };
}

export const ContentStyle = styled.div`
  margin-top: 250px;
  height: 400px;
`;

const UpperBar = styled.div`
  width: 100%;
  height: ${rem(40)};
  color: white;
  background: red;
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 600;
  z-index: 400;
`;

const mapStateToProps = (state) => {
  return {
    defaultTitle: state.general.title,
    token: state.auth.token,
    lang: state.general.lang,
  };
};

export default connect(mapStateToProps)(
  connectSsr({ displayName: 'UrlMapper' })(UrlMapper),
);
